import type { CommunityFeedPostFragment, MediaInput } from 'generated/graphql';
import { CommunityPostStatus } from 'generated/graphql';
import { pickObject } from '@client/new/helpers/general';
import { MEDIA_INPUT_KEYS } from './media.constants';
import {
  HeldEdit,
  MediaItemStatus,
  Post,
  PostMediaItem,
  PostStatus,
  Reaction,
  ReactionType,
  toAuthorId,
  toPostId,
} from './post.types';

export type ServerPost = CommunityFeedPostFragment & {
  author: { email?: string | null; marketing?: boolean | null };
};

type ServerMedia = ServerPost['media'][number];
type ServerReaction = ServerPost['reactions'][number];

const toMediaStatus = (raw: string | null | undefined): MediaItemStatus =>
  raw === 'processing' || raw === 'failed' ? raw : 'ready';

// Reply media lacks `source`, so it stays optional here and pickObject simply
// skips it when absent.
type ServerMediaFields = Pick<
  ServerMedia,
  | 'audio'
  | 'cdn01'
  | 'height'
  | 'image'
  | 'mimeType'
  | 'status'
  | 'uuid'
  | 'video'
  | 'videoSourceType'
  | 'videoThumbnailUrl'
  | 'videoUrl'
  | 'width'
> & { source?: ServerMedia['source'] };

const toMediaInput = (raw: ServerMediaFields): MediaInput =>
  raw.uuid ? { uuid: raw.uuid } : pickObject(raw, MEDIA_INPUT_KEYS);

const toPostStatus = (raw: string | null | undefined): PostStatus => {
  switch (raw) {
    case CommunityPostStatus.Draft:
      return 'draft';
    case CommunityPostStatus.Scheduled:
      return 'scheduled';
    case CommunityPostStatus.Processing:
      return 'processing';
    default:
      return 'published';
  }
};

export const toMediaItem = (raw: ServerMediaFields): PostMediaItem | null => {
  const status = toMediaStatus(raw.status);
  const input = toMediaInput(raw);

  const isAudio = !!raw.audio || (raw.mimeType?.startsWith('audio') ?? false);
  if (isAudio) {
    if (!raw.audio && status === 'ready') return null;
    const cdn = raw.cdn01 ?? '';
    return {
      type: 'audio',
      url: raw.audio ? `${cdn}${raw.audio}` : '',
      thumbnailUrl: '',
      status,
      input,
    };
  }

  const isMux = raw.videoSourceType === 'mux';
  const isVideo = isMux || (raw.mimeType?.startsWith('video') ?? false);

  let url: string;
  let thumbnailUrl: string;
  if (isMux) {
    url = raw.videoUrl ?? '';
    thumbnailUrl = raw.videoThumbnailUrl ?? raw.image ?? '';
  } else {
    const cdn = raw.cdn01 ?? '';
    const source = isVideo ? raw.video : raw.image;
    url = source ? `${cdn}${source}` : '';
    thumbnailUrl = isVideo
      ? raw.image
        ? `${cdn}${raw.image}`
        : ''
      : `${cdn}${raw.image ?? source ?? ''}`;
  }

  // A ready item with no usable source is dropped; processing/failed items are
  // kept so the strip can render their in-progress or error state.
  if (status === 'ready' && !url) return null;

  return {
    type: isVideo ? 'video' : 'image',
    url,
    thumbnailUrl,
    status,
    input,
  };
};

const toReaction = (raw: ServerReaction): Reaction => ({
  type: raw.type,
  count: raw.count,
  isToggled: raw.isToggled,
});

// A creator's published post only carries a hidden media entry while an edit
// is held back, so the rule needs no extra field from the server.
export const toHeldEdit = (
  isCreator: boolean,
  status: PostStatus,
  media: PostMediaItem[],
): HeldEdit => {
  if (!isCreator || status !== 'published') return 'none';
  if (media.some((item) => item.status === 'failed')) return 'failed';
  if (media.some((item) => item.status === 'processing')) return 'processing';
  return 'none';
};

export const toPost = (raw: ServerPost): Post => {
  const isCreator = (raw.author.badges ?? []).includes('creator');
  const status = toPostStatus(raw.status);
  const media = raw.media
    .map(toMediaItem)
    .filter((item): item is PostMediaItem => Boolean(item));
  return {
    id: toPostId(raw.id),
    author: {
      id: toAuthorId(raw.author.id),
      name: raw.author.username,
      avatarUrl: raw.author.profilePicture,
      email: raw.author.email || null,
      marketing: raw.author.marketing ?? null,
      isOwn: (raw.author.badges ?? []).includes('own'),
      isCreator,
      canReceiveDm: raw.author.hasDirectMessagesAccess === true,
    },
    createdAt: new Date(raw.createdAt),
    publishedAt: raw.publishedAt ? new Date(raw.publishedAt) : null,
    content: raw.text,
    edited: raw.edited,
    status,
    media,
    reactions: raw.reactions
      .filter((reaction) => reaction.type !== ReactionType.Comment)
      .map(toReaction),
    commentCount:
      raw.reactions.find((reaction) => reaction.type === ReactionType.Comment)
        ?.count ?? 0,
    pinned: raw.pinned ?? false,
    heldEdit: toHeldEdit(isCreator, status, media),
  };
};
